// src/pages/statistiques/StatsMembres.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert, Table, Form, Button, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';
import * as XLSX from 'xlsx';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

const formatNombre = (nombre) => {
  if (nombre === null || nombre === undefined || isNaN(nombre)) return '0';
  return new Intl.NumberFormat('fr-FR').format(nombre);
};

// Palette de couleurs pour les graphiques
const CHART_COLORS = {
  blue: COLORS.primary,
  green: COLORS.success,
  red: COLORS.danger,
  yellow: COLORS.warning,
  purple: COLORS.purple,
  pink: COLORS.pink,
  cyan: COLORS.cyan,
  gray: COLORS.gray,
  indigo: COLORS.indigo,
  orange: COLORS.secondary,
  teal: '#14b8a6',
  rose: COLORS.rose,
  emerald: '#059669',
  amber: '#d97706',
  sky: '#0ea5e9',
  violet: '#7c3aed',
  fuchsia: '#d946ef',
};

// Palette pour les datasets (10 couleurs distinctes)
const DATASET_COLORS = [
  CHART_COLORS.purple,
  CHART_COLORS.cyan,
  CHART_COLORS.green,
  CHART_COLORS.orange,
  CHART_COLORS.pink,
  CHART_COLORS.blue,
  CHART_COLORS.teal,
  CHART_COLORS.amber,
  CHART_COLORS.rose,
  CHART_COLORS.indigo,
];

export default function StatsMembres() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [rolePage, setRolePage] = useState(1);
  const [rolePerPage] = useState(5);
  const [eglisePage, setEglisePage] = useState(1);
  const [eglisePerPage] = useState(5);
  
  const [statsData, setStatsData] = useState({
    total: 0,
    parGenre: { hommes: 0, femmes: 0, enfants: 0 },
    parRole: [],
    parEglise: [],
    evolution: [],
    naissances: [],
    baptemes: []
  });

  const years = [];
  for (let i = new Date().getFullYear() - 5; i <= new Date().getFullYear(); i++) {
    years.push(i);
  }

  useEffect(() => {
    fetchStats();
    setRolePage(1);
    setEglisePage(1);
  }, [selectedYear]);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const url = `https://josephsbusiness.com/api/stats/membres.php?user_id=${userId}&annee=${selectedYear}`;
      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });

      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
      
      const result = await response.json();
      
      if (result.success) {
        const data = result.data || {};
        setStatsData({
          total: data.total || 0,
          parGenre: {
            hommes: data.parGenre?.hommes || 0,
            femmes: data.parGenre?.femmes || 0,
            enfants: data.parGenre?.enfants || 0
          },
          parRole: Array.isArray(data.parRole) ? data.parRole : [],
          parEglise: Array.isArray(data.parEglise) ? data.parEglise : [],
          evolution: Array.isArray(data.evolution) ? data.evolution : [],
          naissances: Array.isArray(data.naissances) ? data.naissances : [],
          baptemes: Array.isArray(data.baptemes) ? data.baptemes : []
        });
      } else {
        setError(result.message || 'Erreur de chargement des statistiques');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!statsData.parRole.length && !statsData.parEglise.length && !statsData.evolution.length) {
      alert('Aucune donnée à exporter');
      return;
    }

    const wb = XLSX.utils.book_new();
    
    if (statsData.parRole.length > 0) {
      const roleData = statsData.parRole.map(item => ({
        'Rôle': item.role || 'Non spécifié',
        'Nombre': item.count || 0,
        'Pourcentage': statsData.total > 0 ? ((item.count / statsData.total) * 100).toFixed(1) + '%' : '0%'
      }));
      const ws1 = XLSX.utils.json_to_sheet(roleData);
      XLSX.utils.book_append_sheet(wb, ws1, "Par rôle");
    }
    
    if (statsData.parEglise.length > 0) {
      const egliseData = statsData.parEglise.map(item => ({
        'Église': item.eglise || 'Non spécifié',
        'Nombre': item.count || 0,
        'Pourcentage': statsData.total > 0 ? ((item.count / statsData.total) * 100).toFixed(1) + '%' : '0%'
      }));
      const ws2 = XLSX.utils.json_to_sheet(egliseData);
      XLSX.utils.book_append_sheet(wb, ws2, "Par église");
    }
    
    if (statsData.evolution.length > 0) {
      const evolutionData = statsData.evolution.map(item => ({
        'Mois': item.mois || '',
        'Nouveaux inscrits': item.inscrits || 0,
        'Total cumulé': item.totaux || 0
      }));
      const ws3 = XLSX.utils.json_to_sheet(evolutionData);
      XLSX.utils.book_append_sheet(wb, ws3, "Évolution");
    }
    
    XLSX.writeFile(wb, `statistiques_membres_${selectedYear}.xlsx`);
  };

  // Pagination
  const indexOfLastRole = rolePage * rolePerPage;
  const indexOfFirstRole = indexOfLastRole - rolePerPage;
  const currentRoles = statsData.parRole.slice(indexOfFirstRole, indexOfLastRole);
  const totalRolePages = Math.ceil(statsData.parRole.length / rolePerPage);

  const indexOfLastEglise = eglisePage * eglisePerPage;
  const indexOfFirstEglise = indexOfLastEglise - eglisePerPage;
  const currentEglises = statsData.parEglise.slice(indexOfFirstEglise, indexOfLastEglise);
  const totalEglisePages = Math.ceil(statsData.parEglise.length / eglisePerPage);

  const renderPagination = (currentPage, totalPages, setPageFunction) => {
    if (totalPages <= 1) return null;
    
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    return (
      <Pagination size="sm" className="mt-2 mb-0 justify-content-center modern-pagination">
        <Pagination.First onClick={() => setPageFunction(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => setPageFunction(currentPage - 1)} disabled={currentPage === 1} />
        
        {startPage > 1 && (
          <>
            <Pagination.Item onClick={() => setPageFunction(1)}>1</Pagination.Item>
            {startPage > 2 && <Pagination.Ellipsis disabled />}
          </>
        )}
        
        {[...Array(endPage - startPage + 1)].map((_, i) => {
          const page = startPage + i;
          return (
            <Pagination.Item
              key={page}
              active={page === currentPage}
              onClick={() => setPageFunction(page)}
            >
              {page}
            </Pagination.Item>
          );
        })}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
            <Pagination.Item onClick={() => setPageFunction(totalPages)}>
              {totalPages}
            </Pagination.Item>
          </>
        )}
        
        <Pagination.Next onClick={() => setPageFunction(currentPage + 1)} disabled={currentPage === totalPages} />
        <Pagination.Last onClick={() => setPageFunction(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>
    );
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
              <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des statistiques</h5>
              <p className="text-muted">Récupération des données en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const hasGenreData = statsData.parGenre.hommes > 0 || statsData.parGenre.femmes > 0 || statsData.parGenre.enfants > 0;
  const hasRoleData = statsData.parRole.length > 0;
  const hasEgliseData = statsData.parEglise.length > 0;
  const hasEvolutionData = statsData.evolution.some(item => item.inscrits > 0);
  const hasNaissancesData = statsData.naissances.some(item => item.naissances > 0);
  const hasBaptemesData = statsData.baptemes.some(item => item.baptemes > 0);

  // Données pour les graphiques avec couleurs harmonisées
  const genreData = {
    labels: ['Hommes', 'Femmes', 'Enfants'],
    datasets: [{
      data: [
        statsData.parGenre.hommes || 0,
        statsData.parGenre.femmes || 0,
        statsData.parGenre.enfants || 0
      ],
      backgroundColor: [
        CHART_COLORS.cyan,
        CHART_COLORS.pink,
        CHART_COLORS.amber
      ],
      borderColor: 'white',
      borderWidth: 2,
    }],
  };

  const roleData = {
    labels: statsData.parRole.map(item => item.role || 'Non spécifié'),
    datasets: [{
      data: statsData.parRole.map(item => item.count || 0),
      backgroundColor: DATASET_COLORS.slice(0, statsData.parRole.length),
      borderColor: 'white',
      borderWidth: 2,
    }],
  };

  const evolutionData = {
    labels: statsData.evolution.map(item => item.mois),
    datasets: [
      {
        label: 'Nouveaux membres',
        data: statsData.evolution.map(item => item.inscrits || 0),
        backgroundColor: CHART_COLORS.green + '80',
        borderColor: CHART_COLORS.green,
        borderWidth: 2,
      },
      {
        label: 'Total cumulé',
        data: statsData.evolution.map(item => item.totaux || 0),
        backgroundColor: CHART_COLORS.blue + '80',
        borderColor: CHART_COLORS.blue,
        borderWidth: 2,
      },
    ],
  };

  const naissancesBaptemesData = {
    labels: statsData.naissances.map(item => item.mois),
    datasets: [
      {
        label: 'Naissances',
        data: statsData.naissances.map(item => item.naissances || 0),
        backgroundColor: CHART_COLORS.pink + '80',
        borderColor: CHART_COLORS.pink,
        borderWidth: 2,
        hidden: !hasNaissancesData,
      },
      {
        label: 'Baptêmes',
        data: statsData.baptemes.map(item => item.baptemes || 0),
        backgroundColor: CHART_COLORS.purple + '80',
        borderColor: CHART_COLORS.purple,
        borderWidth: 2,
        hidden: !hasBaptemesData,
      },
    ],
  };

  const egliseData = {
    labels: statsData.parEglise.map(item => item.eglise || 'Non spécifié'),
    datasets: [{
      data: statsData.parEglise.map(item => item.count || 0),
      backgroundColor: DATASET_COLORS.slice(0, statsData.parEglise.length),
      borderColor: 'white',
      borderWidth: 2,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { size: 10 },
          padding: 15,
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.raw;
            return `${label}: ${formatNombre(value)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: function(value) { return Number.isInteger(value) ? value : ''; }
        }
      }
    }
  };

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid px-0 px-lg-3">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.indigo})` }}>
                <i className="ri-group-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Statistiques des membres</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-group-2-line"></i> Total {formatNombre(statsData.total)}
                  </span>
                  <span className="badge-modern" style={{ background: `${CHART_COLORS.cyan}20`, color: CHART_COLORS.cyan }}>
                    <i className="ri-men-line"></i> Hommes {formatNombre(statsData.parGenre.hommes)}
                  </span>
                  <span className="badge-modern" style={{ background: `${CHART_COLORS.pink}20`, color: CHART_COLORS.pink }}>
                    <i className="ri-women-line"></i> Femmes {formatNombre(statsData.parGenre.femmes)}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Form.Select
                size="sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ width: '100px', border: '1px solid #d1d5db', background: 'white' }}
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Form.Select>
              <button className="btn-modern-outline" onClick={exportToExcel}>
                <i className="ri-file-excel-line"></i> Excel
              </button>
              <button className="btn-modern-outline" onClick={fetchStats} disabled={loading}>
                <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i> Actualiser
              </button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="mb-3" style={{ borderLeft: `4px solid ${COLORS.danger}` }}>
              <div className="d-flex align-items-center">
                <i className="ri-error-warning-line me-2" style={{ color: COLORS.danger, fontSize: '1.2rem' }}></i>
                <span>{error}</span>
              </div>
            </Alert>
          )}

          {/* ===== KPI CARDS ===== */}
          <Row className="g-3 mb-4">
            <Col xs="6" xl="3">
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-group-2-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Total membres</span>
                  <h3 className="kpi-value">{formatNombre(statsData.total)}</h3>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: CHART_COLORS.cyan }}>
                <div className="kpi-icon" style={{ background: `${CHART_COLORS.cyan}20`, color: CHART_COLORS.cyan }}>
                  <i className="ri-men-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Hommes</span>
                  <h3 className="kpi-value">{formatNombre(statsData.parGenre.hommes)}</h3>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: CHART_COLORS.pink }}>
                <div className="kpi-icon" style={{ background: `${CHART_COLORS.pink}20`, color: CHART_COLORS.pink }}>
                  <i className="ri-women-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Femmes</span>
                  <h3 className="kpi-value">{formatNombre(statsData.parGenre.femmes)}</h3>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: CHART_COLORS.amber }}>
                <div className="kpi-icon" style={{ background: `${CHART_COLORS.amber}20`, color: CHART_COLORS.amber }}>
                  <i className="ri-user-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Enfants</span>
                  <h3 className="kpi-value">{formatNombre(statsData.parGenre.enfants)}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== GRAPHIQUES ===== */}
          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="chart-card-modern">
                <Card.Header className="chart-header">
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                    <i className="ri-pie-chart-line me-2" style={{ color: CHART_COLORS.blue }}></i>
                    Répartition par genre
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '280px', padding: '1rem' }}>
                  {hasGenreData ? <Pie data={genreData} options={chartOptions} /> : <div className="text-center text-muted small py-5">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="chart-card-modern">
                <Card.Header className="chart-header">
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.purple, fontSize: '0.95rem' }}>
                    <i className="ri-user-star-line me-2" style={{ color: CHART_COLORS.purple }}></i>
                    Répartition par rôle
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '280px', padding: '1rem' }}>
                  {hasRoleData ? <Pie data={roleData} options={chartOptions} /> : <div className="text-center text-muted small py-5">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="chart-card-modern">
                <Card.Header className="chart-header">
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.success, fontSize: '0.95rem' }}>
                    <i className="ri-bar-chart-line me-2" style={{ color: CHART_COLORS.green }}></i>
                    Évolution des membres ({selectedYear})
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '280px', padding: '1rem' }}>
                  {hasEvolutionData ? <Bar data={evolutionData} options={chartOptions} /> : <div className="text-center text-muted small py-5">Aucune donnée d'évolution disponible</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="chart-card-modern">
                <Card.Header className="chart-header">
                  <h6 className="fw-semibold mb-0" style={{ color: CHART_COLORS.pink, fontSize: '0.95rem' }}>
                    <i className="ri-user-line me-2" style={{ color: CHART_COLORS.pink }}></i>
                    Naissances et baptêmes ({selectedYear})
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '280px', padding: '1rem' }}>
                  {hasNaissancesData || hasBaptemesData ? <Bar data={naissancesBaptemesData} options={chartOptions} /> : <div className="text-center text-muted small py-5">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col md={12}>
              <Card className="chart-card-modern">
                <Card.Header className="chart-header">
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.secondary, fontSize: '0.95rem' }}>
                    <i className="ri-building-line me-2" style={{ color: CHART_COLORS.orange }}></i>
                    Répartition par église
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '300px', padding: '1rem' }}>
                  {hasEgliseData ? <Bar data={egliseData} options={{ ...chartOptions, indexAxis: 'y' }} /> : <div className="text-center text-muted small py-5">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ===== TABLEAUX ===== */}
          <Row className="mt-4">
            <Col md={6}>
              <Card className="table-card-modern">
                <Card.Header className="table-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="fw-semibold mb-0" style={{ color: COLORS.purple, fontSize: '0.95rem' }}>
                      <i className="ri-group-line me-2" style={{ color: CHART_COLORS.purple }}></i>
                      Répartition par rôle
                    </h6>
                    {hasRoleData && <span className="badge-count">{statsData.parRole.length} rôle{statsData.parRole.length > 1 ? 's' : ''}</span>}
                  </div>
                </Card.Header>
                <Card.Body className="p-0 d-flex flex-column" style={{ flex: 1 }}>
                  {hasRoleData ? (
                    <>
                      <div style={{ flex: 1, overflowX: 'auto' }}>
                        <Table hover size="sm" className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="px-3 py-2">Rôle</th>
                              <th className="px-3 py-2 text-end">Nombre</th>
                              <th className="px-3 py-2 text-end">Pourcentage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentRoles.map((item, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2">{item.role || 'Non spécifié'}</td>
                                <td className="px-3 py-2 text-end fw-semibold">{item.count || 0}</td>
                                <td className="px-3 py-2 text-end">
                                  <span style={{ color: COLORS.purple }}>
                                    {statsData.total > 0 ? ((item.count / statsData.total) * 100).toFixed(1) : '0'}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {renderPagination(rolePage, totalRolePages, setRolePage)}
                    </>
                  ) : <div className="text-center py-4 text-muted small">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="table-card-modern">
                <Card.Header className="table-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="fw-semibold mb-0" style={{ color: COLORS.secondary, fontSize: '0.95rem' }}>
                      <i className="ri-building-line me-2" style={{ color: CHART_COLORS.orange }}></i>
                      Répartition par église
                    </h6>
                    {hasEgliseData && <span className="badge-count">{statsData.parEglise.length} église{statsData.parEglise.length > 1 ? 's' : ''}</span>}
                  </div>
                </Card.Header>
                <Card.Body className="p-0 d-flex flex-column" style={{ flex: 1 }}>
                  {hasEgliseData ? (
                    <>
                      <div style={{ flex: 1, overflowX: 'auto' }}>
                        <Table hover size="sm" className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="px-3 py-2">Église</th>
                              <th className="px-3 py-2 text-end">Nombre</th>
                              <th className="px-3 py-2 text-end">Pourcentage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentEglises.map((item, index) => (
                              <tr key={index}>
                                <td className="px-3 py-2">{item.eglise || 'Non spécifié'}</td>
                                <td className="px-3 py-2 text-end fw-semibold">{item.count || 0}</td>
                                <td className="px-3 py-2 text-end">
                                  <span style={{ color: COLORS.secondary }}>
                                    {statsData.total > 0 ? ((item.count / statsData.total) * 100).toFixed(1) : '0'}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {renderPagination(eglisePage, totalEglisePages, setEglisePage)}
                    </>
                  ) : <div className="text-center py-4 text-muted small">Aucune donnée disponible</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>

      {/* ===== STYLES ===== */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .header-modern {
          background: white;
          padding: 1.2rem 1.8rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border-left: 4px solid ${COLORS.secondary};
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.4rem;
        }
        .gradient-text {
          color: #1e293b;
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
        }
        .badge-modern {
          background: #f1f4f9;
          color: #475569;
          padding: 0.3rem 0.8rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .badge-modern i {
          color: ${COLORS.secondary};
        }
        .btn-modern-outline {
          border: 1px solid #d1d5db;
          background: white;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-outline:hover {
          border-color: ${COLORS.secondary};
          color: ${COLORS.secondary};
          background: #fafafa;
        }

        .kpi-card {
          background: white;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: ${COLORS.primary}; }
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: rgba(41, 90, 167, 0.1); color: ${COLORS.primary}; }
        .kpi-content { flex: 1; }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .fs-24 { font-size: 1.5rem; }

        .chart-card-modern {
          background: white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
        }
        .chart-card-modern:hover {
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .chart-header {
          background: transparent;
          border-bottom: 1px solid #e9edf2;
          padding: 0.75rem 1.25rem;
        }

        .table-card-modern {
          background: white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        .table-card-modern:hover {
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .table-card-modern .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .table-header {
          background: transparent;
          border-bottom: 1px solid #e9edf2;
          padding: 0.75rem 1.25rem;
        }
        .table-light {
          background: #f8fafc;
        }
        .badge-count {
          background: rgba(0,0,0,0.05);
          color: #475569;
          padding: 0.2rem 0.6rem;
          border-radius: 30px;
          font-size: 0.7rem;
          font-weight: 500;
        }

        .modern-pagination .page-link {
          border: none;
          margin: 0 2px;
          border-radius: 4px;
          color: #64748b;
          font-size: 0.75rem;
          padding: 0.3rem 0.6rem;
        }
        .modern-pagination .page-link:hover {
          background: ${COLORS.primary};
          color: white;
        }
        .modern-pagination .page-item.active .page-link {
          background: ${COLORS.primary};
          color: white;
        }

        @media (max-width: 768px) {
          .header-modern {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            padding: 1rem;
          }
          .header-left {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </>
  );
}